import React from 'react';
import {Text, TouchableOpacity, View} from 'react-native';
import i18n from 'i18n-js';
import {fontFamily} from '../../../helpers/fontFamily';
import {colors} from '../../../style/rulesEngine';
import {
  accentColor,
  dangerColor,
  textMutedColor,
  touchTargetMin
} from '../../../style/adminTools';
import {dangerTextOnDark} from '../../../style/adminTenantGrid';
import ShimmerRows from '../tenantGrid/ShimmerRows';
import {
  Body,
  PanelButton,
  PanelFrame,
  Title
} from '../tenantGrid/GridStateBanner';

const t = (key, defaultValue, options) =>
  i18n.t(`adminTenantPage.${key}`, {defaultValue, ...options});

const retryLabel = () => i18n.t('adminTenantGrid.retry');

const failureStatusText = error => {
  if (!error) return '';
  return error.status === null || error.status === undefined
    ? error.kind || ''
    : String(error.status);
};

export const TenantLoadingState = ({tenantName}) => (
  <View testID="tenantPageLoading" accessibilityState={{busy: true}}>
    <Text
      style={{
        marginBottom: 12,
        fontFamily: fontFamily.font500,
        fontSize: 14,
        color: textMutedColor
      }}
    >
      {t('loading', 'Loading {{tenant}}...', {tenant: tenantName})}
    </Text>
    <ShimmerRows count={4} testID="tenantPageSkeleton" />
  </View>
);

const failureCopy = (kind, tenantName, error) => {
  if (kind === 'notFound') {
    return {
      title: t('notFoundTitle', "{{tenant}} doesn't exist anymore", {
        tenant: tenantName
      }),
      body: error.message
    };
  }
  if (kind === 'unauthorized' || kind === 'forbidden') {
    return {
      title: t(
        'forbidden',
        'Your account no longer has team access. Sign in again.'
      ),
      body: error.message
    };
  }
  return {
    title: t('loadFailedTitle', "Couldn't load {{tenant}}", {
      tenant: tenantName
    }),
    body: t(
      'loadFailedBody',
      "The server didn't answer ({{status}}). No settings are shown because none could be read, and nothing was changed.",
      {status: failureStatusText(error)}
    )
  };
};

export const TenantFailurePanel = ({
  tenantName,
  error,
  onRetry,
  onBack,
  phone
}) => {
  const {title, body} = failureCopy(
    error && error.kind,
    tenantName,
    error || {}
  );
  return (
    <PanelFrame testID="tenantPageFailedPanel" phone={phone}>
      <Title>{title}</Title>
      <Body>{body}</Body>
      <View style={phone ? {alignSelf: 'stretch'} : {flexDirection: 'row'}}>
        <PanelButton
          primary
          block={phone}
          testID="tenantPageRetry"
          label={retryLabel()}
          onPress={onRetry}
        />
        <PanelButton
          block={phone}
          testID="tenantPageBack"
          label={t('backToTenants', 'Back to tenants')}
          onPress={onBack}
        />
      </View>
    </PanelFrame>
  );
};

export const TenantRefreshFailedBanner = ({onRetry, phone}) => (
  <View
    testID="tenantPageRefreshFailedBanner"
    accessibilityRole="alert"
    style={{
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 12,
      paddingHorizontal: 14,
      paddingVertical: 10,
      backgroundColor: colors.dangerLight,
      borderWidth: 1,
      borderColor: dangerColor,
      borderRadius: 6
    }}
  >
    <Text
      style={{
        flex: 1,
        fontFamily: fontFamily.font500,
        fontSize: 14,
        color: dangerTextOnDark
      }}
    >
      {t(
        'loadFailedBanner',
        "Couldn't refresh this page. The settings shown may be out of date."
      )}
    </Text>
    <TouchableOpacity
      testID="tenantPageBannerRetry"
      accessibilityRole="button"
      onPress={onRetry}
      style={
        phone
          ? {
              minWidth: touchTargetMin,
              minHeight: touchTargetMin,
              justifyContent: 'center',
              alignItems: 'center'
            }
          : undefined
      }
    >
      <Text
        style={{
          fontFamily: fontFamily.font700,
          fontSize: 14,
          color: accentColor
        }}
      >
        {retryLabel()}
      </Text>
    </TouchableOpacity>
  </View>
);
